import axios from "axios";
import { installSecureChannel } from "./secure-channel";

// The session lives in httpOnly cookies set by the API, so JavaScript never holds a token.
// Requests to our API send those cookies plus the header the API uses to recognise this UI
// (it also makes cross-site cookie requests fail CORS preflight). Other hosts such as S3 or
// Tableau are left untouched.
const API_URL = process.env.REACT_APP_API_URL || "";
const CLIENT_HEADER = "X-Requested-With";
const CLIENT_HEADER_VALUE = "ft-ui";

// Endpoints whose 401 means "not signed in", not "access token expired"
const SESSION_ENDPOINTS = ["authtoken", "auth/code-exchange", "auth/refresh", "auth/logout"];

const isApiRequest = (url = "") => !!API_URL && url.startsWith(API_URL);
const isSessionEndpoint = (url = "") => SESSION_ENDPOINTS.some((endpoint) => url.startsWith(`${API_URL}${endpoint}`));

// Encrypt request/response bodies. Installed first so the interceptors below see decrypted responses.
if (API_URL) {
  installSecureChannel(axios, {
    apiUrl: API_URL,
    handshakePath: "auth/channel",
    info: "ft-channel-v1",
    clientHeaders: { [CLIENT_HEADER]: CLIENT_HEADER_VALUE },
  });
}

axios.interceptors.request.use((config) => {
  if (isApiRequest(config.url)) {
    config.withCredentials = true;
    config.headers[CLIENT_HEADER] = CLIENT_HEADER_VALUE;
  }
  return config;
});

// One refresh at a time; concurrent 401s wait for the same call
let pendingRefresh = null;
const refreshSession = () => {
  if (!pendingRefresh) {
    pendingRefresh = axios.post(`${API_URL}auth/refresh`).finally(() => {
      pendingRefresh = null;
    });
  }
  return pendingRefresh;
};

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { config, response } = error;
    const shouldRefresh = config
      && !config._retriedAfterRefresh
      && response?.status === 401
      && isApiRequest(config.url)
      && !isSessionEndpoint(config.url)
      // A newer login elsewhere ended this session; callers show the "logged out" prompt
      && response?.data?.message !== "New login found.";
    if (!shouldRefresh) throw error;

    config._retriedAfterRefresh = true;
    try {
      await refreshSession();
    } catch (refreshError) {
      // The refresh's own answer says why the session can't continue ("New login found.", "Session has
      // ended.", "Account Validity Expired.") so callers show that instead of the bare 401. A 429 keeps its
      // status, so callers report the rate limit without logging the user out.
      if (refreshError?.response) throw refreshError;
      throw error;
    }
    return axios(config);
  }
);

// Ends the server session and clears the auth cookies
export const endSession = () => axios.post(`${API_URL}auth/logout`).catch(() => null);
